import type { Quote } from '../api/types'
import { QuoteLineIdentity } from '../ui/product'
import {
  equipmentRows,
  formatDiscountChain,
  quoteLineDetail,
  quoteLineNumbers,
  quoteLinePricing,
} from '../ui/quote-lines'
import { numberValue } from '../ui/numbers'
import { Money, PercentValue } from '../ui/values'
import { cn } from '@/lib/utils'
import { quoteNet, quoteProfit, quoteRebate, quoteSell } from './data'

/** A quote's equipment and services with per-line Net, sell and Profit, then the quote totals. */
export function QuoteEquipment({ quote }: { quote: Quote }) {
  const rows = equipmentRows([...quote.lines].sort((a, b) => a.position - b.position))
  const numbers = quoteLineNumbers(rows)
  return (
    <section
      aria-label={`Equipment in ${quote.quote_number}`}
      className="m-3 overflow-hidden rounded-lg border bg-card sm:m-4"
      id={`items-${quote.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2.5">
        <h3 className="text-sm font-semibold">
          {quote.quote_number} <span className="ml-1 font-normal text-muted-foreground">Equipment & services</span>
        </h3>
        <span className="text-xs text-muted-foreground">
          {quote.lines.length} priced {quote.lines.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      {rows.length ? (
        <ol className="divide-y">
          {rows.map((line) => {
            const config = Boolean(line.configuration_group)
            const child = Boolean(line.parent_line_id)
            const pricing = quoteLinePricing(line)
            const detail = quoteLineDetail(line)
            const specs = (line.equipment_details ?? []).filter((text) => {
              const key = text.split(':', 1)[0]!.toLowerCase()
              return (
                !line.parent_equipment_details?.includes(text) &&
                !Object.keys(line.configuration).some((configured) => configured.toLowerCase() === key) &&
                !(config && (key === line.configuration_group?.toLowerCase() || ['dimensions', 'capacity'].includes(key)))
              )
            })
            const kind = config
              ? line.configuration_group
              : child
                ? 'Accessory'
                : line.taxonomy_label || (line.line_type === 'service' ? 'Service' : 'Equipment')
            return (
              <li
                className={cn(
                  'relative grid min-w-0 grid-cols-2 gap-x-4 gap-y-2 px-3 py-3 text-sm lg:grid-cols-[minmax(0,1fr)_64px_150px_170px]',
                  child && 'ml-4 border-l bg-muted/30 sm:ml-8',
                )}
                data-line-kind={config ? 'configuration' : child ? 'accessory' : 'root'}
                key={line.id}
              >
                <div className="col-span-2 flex min-w-0 items-start gap-2 lg:col-span-1">
                  <span className="w-7 shrink-0 pt-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                    {numbers.get(line.id)}
                  </span>
                  <QuoteLineIdentity
                    className="min-w-0 flex-1"
                    imageClassName="h-12 w-12 bg-white object-contain p-0.5"
                    line={line}
                    title={config ? line.description : undefined}
                  >
                    <span className="block">{kind}</span>
                    {!config && detail && detail !== line.model ? <span className="mt-1 block">{detail}</span> : null}
                    {specs.length ? (
                      <details className="mt-1">
                        <summary className="w-fit cursor-pointer rounded py-1 font-medium text-foreground">Specifications</summary>
                        <ul className="mt-1 space-y-1">
                          {specs.map((spec) => (
                            <li className="break-words" key={spec}>{spec}</li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                    {!child && line.fulfillment ? (
                      <span className="mt-1.5 block">
                        <span className="font-medium text-foreground">{line.fulfillment.availability.label}</span>
                        {' · '}
                        {line.fulfillment.lead_time.label}
                      </span>
                    ) : null}
                  </QuoteLineIdentity>
                </div>
                <div className={cn('ml-9 lg:col-span-1 lg:ml-0 lg:text-right', !config && 'col-span-2')}>
                  <span className="text-xs text-muted-foreground">Qty </span>
                  <span className="font-medium tabular-nums">{numberValue(line.quantity).toLocaleString('en-US')}</span>
                </div>
                {config ? (
                  <p className="text-right text-xs text-muted-foreground lg:col-span-2">
                    Included in {numbers.get(line.parent_line_id!)}
                  </p>
                ) : (
                  <div className="col-span-2 ml-9 grid grid-cols-2 gap-3 lg:contents">
                    <div className="space-y-0.5 lg:text-right">
                      <p><span className="text-xs text-muted-foreground">List </span><Money value={line.list_amount} /></p>
                      <p className="text-xs text-muted-foreground">{formatDiscountChain(line.discount_chain) || '-'}</p>
                      <p><span className="text-xs text-muted-foreground">Net </span><Money value={pricing.net} /></p>
                    </div>
                    <div className="space-y-0.5 text-right">
                      <p><span className="text-xs text-muted-foreground">Sell total </span><Money className="font-semibold" value={pricing.sellTotal} /></p>
                      <p className="text-xs text-muted-foreground">
                        <Money className="font-normal" value={pricing.sell} /> each · <PercentValue value={pricing.margin} />
                      </p>
                      <p className="text-xs text-muted-foreground"><Money className="font-normal" value={pricing.profit} /> profit</p>
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      ) : (
        <p className="p-4 text-sm text-muted-foreground">No equipment added</p>
      )}
      <dl className="flex flex-wrap justify-end gap-x-8 gap-y-2 border-t bg-muted/40 px-3 py-3 text-sm">
        <div><dt className="text-xs text-muted-foreground">Net</dt><dd><Money value={quoteNet(quote)} /></dd></div>
        <div><dt className="text-xs text-muted-foreground">Profit</dt><dd><Money value={quoteProfit(quote)} /></dd></div>
        {quoteRebate(quote) > 0 ? (
          <div><dt className="text-xs text-muted-foreground">Rebate</dt><dd><Money value={quoteRebate(quote)} /></dd></div>
        ) : null}
        <div><dt className="text-xs text-muted-foreground">Sell total</dt><dd><Money className="font-semibold" value={quoteSell(quote)} /></dd></div>
      </dl>
    </section>
  )
}
